/** Mission Agent screen for one device: /missionagent/:deviceId.
 *
 *  The mission screen from the mission-agent dashboard (same panels, same
 *  brand theme), fed by the sentio read API (api/missionagent/...) and kept
 *  live by the device's Mission Agent socket. The header carries the mission,
 *  the device status, the latest observations and the mission summary's
 *  situation and copilot assessment; below it, the handoff draft, the Mission
 *  Timeline (key moments), and open tasks with Recent Updates (everything,
 *  newest first). The patient care record is a PDF download in the top bar.
 *
 *  Read-only: drafts show with their sources; signing is not offered yet.
 */

import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import { styled, ThemeProvider } from "@mui/material/styles";

import { MissionCloseoutButton } from "./components/MissionCloseoutButton";
import { EndMissionButton } from "./components/EndMissionButton";
import { DraftPanel } from "./components/DraftPanel";
import { DebugInputsButton } from "./components/DebugInputsPanel";
import { SimulateMissionButton } from "./components/SimulateMissionPanel";
import { MissionHeader, MissionSummaryNote } from "./components/MissionHeader";
import { MissionPicker } from "./components/MissionPicker";
import { MissionTimeline } from "./components/MissionTimeline";
import { OpenTasksPanel } from "./components/OpenTasksPanel";
import { RecentUpdates } from "./components/RecentUpdates";
import { VitalsPanel } from "./components/VitalsPanel";
import { theme } from "./theme";
import { useMission } from "./useMission";

const Page = styled(Box)(({ theme }) => ({
  background: theme.palette.background.default,
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  minHeight: "100%",
}));

const Shell = styled(Container)(({ theme }) => ({
  paddingTop: 0,
  paddingBottom: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const TopBar = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  paddingTop: theme.spacing(2),
  // Room for the one-line note hung under Generate ePCR.
  paddingBottom: theme.spacing(1.5),
}));

const Overline = styled(Typography)(({ theme }) => ({
  ...theme.typography.overline,
  color: theme.palette.text.secondary,
  display: "block",
}));

/** Status and drafts | timeline and updates | tasks and the agent. */
const Columns = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr)",
  gap: theme.spacing(2),
  alignItems: "start",
  [theme.breakpoints.down("lg")]: { gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)" },
  [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0, 1fr)" },
}));

const Column = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  minWidth: 0,
}));

const Notice = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
}));

export default function MissionAgent() {
  const { deviceId } = useParams();
  const m = useMission(deviceId);
  const device = m.dashboard?.device;
  // Bumped when End mission starts the ePCR, so the ePCR button re-reads it.
  const [closeoutKey, setCloseoutKey] = useState(0);

  return (
    <ThemeProvider theme={theme}>
      <Page>
        <Shell>
          <TopBar>
            <Box>
              <Overline>Mission Agent</Overline>
              <Typography variant="h1" component="h1">
                {device?.nickname || device?.device_type || `Device ${deviceId}`}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              {m.missions && m.missions.length > 0 ? (
                <MissionPicker
                  missions={m.missions}
                  missionId={m.missionId}
                  following={m.following}
                  onSelect={m.selectMission}
                />
              ) : null}
              <MissionCloseoutButton
                missionId={m.dashboard ? m.dashboard.mission_id : null}
                incident={m.selected?.intervention_id ?? m.dashboard?.mission_id ?? "mission"}
                refreshKey={closeoutKey}
              />
              {/* TEMPORARY: raw Sentio inputs, for development only. */}
              {deviceId ? (
                <DebugInputsButton deviceId={deviceId} interventionId={m.selected?.intervention_id ?? null} />
              ) : null}
              {/* Development only: shown where the API enables simulation. */}
              {deviceId ? <SimulateMissionButton deviceId={deviceId} onNewMission={m.followNewMission} /> : null}
              {/* Last, at the far right of the row. */}
              {deviceId ? (
                <EndMissionButton
                  deviceId={deviceId}
                  missionId={m.dashboard ? m.dashboard.mission_id : null}
                  incident={m.selected?.intervention_id ?? m.dashboard?.mission_id ?? "mission"}
                  endedAt={m.dashboard?.ended_at ?? null}
                  endedBy={m.dashboard?.ended_by ?? null}
                  onEnded={() => {
                    setCloseoutKey((key) => key + 1);
                    // The engine reads the End Mission event within a moment or two.
                    void m.refresh();
                    setTimeout(() => void m.refresh(), 2500);
                  }}
                />
              ) : null}
            </Box>
          </TopBar>

          {m.error ? <Alert severity="error">{m.error}</Alert> : null}
          {m.missions === null || (m.loading && !m.dashboard) ? <CircularProgress size={24} /> : null}

          {m.missions !== null && m.missions.length === 0 ? (
            <>
              <Notice>
                No mission recorded for this device yet. One starts when the device reports an intervention;
                this page follows it automatically.
              </Notice>
            </>
          ) : null}

          {m.selected && !m.selected.in_engine ? (
            <>
              <Notice>
                Sentio recorded mission {m.selected.intervention_id}, but the Mission Agent engine never processed it
                (it was not running then), so there is nothing to show.
              </Notice>
            </>
          ) : null}

          {m.dashboard ? (
            <>
              <MissionHeader
                dashboard={m.dashboard}
                live={m.live}
                liveDeviceStatus={m.following ? m.liveStatus.status : null}
                liveStreaming={m.following ? m.liveStatus.streaming : null}
                footer={<MissionSummaryNote dashboard={m.dashboard} />}
              >
                <VitalsPanel
                  embedded
                  latest={m.dashboard.latest_vitals}
                  observations={m.dashboard.observations}
                />
              </MissionHeader>
              <Columns>
                <Column>
                  <DraftPanel
                    title="Receiving facility handoff"
                    artifact={m.dashboard.artifacts.find((a) => a.kind === "handoff_draft")}
                    emptyText="No handoff yet. It is drafted as the mission records facts."
                    progress={m.dashboard.drafting?.handoff_draft}
                    what="handoff"
                    zone={m.dashboard.time_zone}
                  />
                </Column>
                <Column>
                  <MissionTimeline dashboard={m.dashboard} />
                </Column>
                <Column>
                  <OpenTasksPanel
                    tasks={m.dashboard.open_tasks}
                    missingFields={m.dashboard.required_fields_missing}
                    handoffReadiness={m.dashboard.handoff_readiness}
                  />
                  <RecentUpdates items={m.updates} />
                </Column>
              </Columns>
            </>
          ) : null}
        </Shell>
      </Page>
    </ThemeProvider>
  );
}
